import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import TextMAS from "components/TextMAS/TextMAS";
import s from "./StatisticsOfflineTransaction.module.scss";
import { fetchStatisticsChange } from "./model";
import AnalyticsTable from "components/AnalyticsTable/AnalyticsTable";
import { amountConversion } from "utils/utils";
import useSnackbarMAS from "utils/useSnackbarMAS/useSnackbarMAS";
import { useAuth } from "utils/useAuth/useAuth";
import Card from "components/Card/Card";
import { Stack } from "@mui/material";
import PageContainer from "components/PageContainer/PageContainer";

const PAGE_LOCALE_KEY = "page.analytics.systemStatistics.offlineTransaction.";
const NO_DATA_PLACEHOLDER = "-";

const StatisticsOfflineTransaction = ({ focus }) => {
  const { t } = useTranslation();
  const { snackbarError } = useSnackbarMAS();
  const { logout } = useAuth();

  const [isChangeLoading, setIsChangeLoading] = useState(true);
  const [isChangeError, setIsChangeError] = useState(false);
  const isLoading = isChangeLoading;
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  const [dataSource, setDataSource] = useState({
    yearlyStats: [],
    monthlyStats: [],
    byCardType: [],
  });

  useEffect(() => {
    if (focus && !hasFetched) {
      getStatisticsChange();
      setHasFetched(true);
    }
  }, [focus]);

  const apiStatusHandler = (status, setIsLoading, setError, errorToast) => {
    switch (status) {
      case 401: {
        console.log("You have no authorization to view the page");
        return logout("TOKEN_EXPIRED");
      }
      case 403: {
        setIsUnauthorized(true);
        setIsLoading(false);
        return;
      }
      default: {
        errorToast();
        setError(true);
        setIsLoading(false);
        return;
      }
      case 200:
        break;
    }
  };

  const getStatisticsChange = async () => {
    const showErrorToast = () =>
      snackbarError({
        message: t(PAGE_LOCALE_KEY + "fetchingError.transactionStat"),
      });
    setIsChangeLoading(true);
    const response = await fetchStatisticsChange();

    if (!response) {
      setIsChangeError("networkError");
      setIsChangeLoading(false);
      return;
    }

    apiStatusHandler(
      response?.status,
      setIsChangeLoading,
      setIsChangeError,
      showErrorToast,
    );

    const result = response?.data;
    switch (result?.responseCode) {
      case "0000":
        setDataSource((prev) => ({ ...prev, ...result.data }));
        break;
      case "1200":
        setIsUnauthorized(true);
        break;
      default:
        showErrorToast();
        break;
    }
    setIsChangeLoading(false);
  };

  const renderTransactionChangeCell = (
    count,
    countChange,
    changePercentage,
    currency,
  ) => {
    const statChangeColorClass = !countChange
      ? s["change-stat-grey"]
      : countChange > 0
        ? s["change-stat-green"]
        : s["change-stat-red"];

    const amountSymbol = !countChange ? "" : countChange > 0 ? "+" : "-";

    const amountText = (
      <TextMAS fontSize="18px">
        {(currency?.concat(" ") ?? "") + amountConversion(count)}
      </TextMAS>
    );

    return (
      <div
        className={
          currency ? s["volume-cell-container"] : s["count-cell-container"]
        }
      >
        {amountText}
        <div
          className={`${s["change-stat-container"]} ${statChangeColorClass}`}
        >
          <TextMAS fontSize="small" className={statChangeColorClass}>
            {amountSymbol + amountConversion(Math.abs(countChange))}
          </TextMAS>
          &nbsp;
          <TextMAS fontSize="small" className={statChangeColorClass}>
            {"(" + (changePercentage ?? "--") + "%)"}
          </TextMAS>
        </div>
      </div>
    );
  };

  const transactionYearChangeColumns = [
    {
      id: "year",
      title: t(PAGE_LOCALE_KEY + "transactionYearChange.columnTitle.1"),
      key: "date",
      headerStyle: { width: "160px" },
      render: ({ data, key }) => {
        return <TextMAS fontSize="large">{data[key]}</TextMAS>;
      },
    },
    {
      id: "volume",
      title: t(PAGE_LOCALE_KEY + "transactionYearChange.columnTitle.3"),
      key: "totalVolume",
      render: ({ data, key }) => {
        return renderTransactionChangeCell(
          data[key],
          data[key + "Change"],
          data[key + "ChangePercentage"],
          "HKD",
        );
      },
    },
    {
      id: "count",
      title: t(PAGE_LOCALE_KEY + "transactionYearChange.columnTitle.2"),
      key: "totalCount",
      render: ({ data, key }) => {
        return renderTransactionChangeCell(
          data[key],
          data[key + "Change"],
          data[key + "ChangePercentage"],
        );
      },
    },
  ];

  const transactionMonthChangeColumns = [
    {
      id: "year",
      title: t(PAGE_LOCALE_KEY + "transactionMonthChange.columnTitle.1"),
      key: "date",
      headerStyle: { width: "160px" },
      render: ({ data, key }) => {
        return <TextMAS fontSize="large">{data[key]}</TextMAS>;
      },
    },
    {
      id: "volume",
      title: t(PAGE_LOCALE_KEY + "transactionMonthChange.columnTitle.3"),
      key: "totalVolume",
      render: ({ data, key }) => {
        return renderTransactionChangeCell(
          data[key],
          data[key + "Change"],
          data[key + "ChangePercentage"],
          "HKD",
        );
      },
    },
    {
      id: "count",
      title: t(PAGE_LOCALE_KEY + "transactionMonthChange.columnTitle.2"),
      key: "totalCount",
      render: ({ data, key }) => {
        return renderTransactionChangeCell(
          data[key],
          data[key + "Change"],
          data[key + "ChangePercentage"],
        );
      },
    },
  ];

  return (
    <PageContainer
      isLoading={isLoading}
      unauthorized={isUnauthorized}
      errorMessage={isChangeError}
    >
      <div className={s["analytics-transaction-offline-container"]}>
        <div className={s["last-modified-container"]}>
          <TextMAS>
            {t(PAGE_LOCALE_KEY + "lastUpdated") + ": "}
            {dataSource?.latestUpdate
              ? moment(dataSource?.latestUpdate).format("YYYY-MM-DD HH:mm")
              : NO_DATA_PLACEHOLDER}
          </TextMAS>
        </div>

        <Stack className={s["transaction-info-card-container"]}>
          <Card>
            <Stack direction="column">
              <TextMAS className={s["transaction-info-card-title"]}>
                {t(PAGE_LOCALE_KEY + "infoCard.transMonth.title")}
              </TextMAS>
              <TextMAS className={s["transaction-info-card-amount"]}>
                {`HKD ${
                  amountConversion(
                    dataSource?.monthlyStats?.[0]?.totalVolume,
                  ) ?? "-"
                } (${
                  amountConversion(dataSource?.monthlyStats?.[0]?.totalCount) ??
                  "-"
                })`}
              </TextMAS>
            </Stack>
          </Card>
          <Card>
            <Stack direction="column">
              <TextMAS className={s["transaction-info-card-title"]}>
                {t(PAGE_LOCALE_KEY + "infoCard.transYear.title")}
              </TextMAS>
              <TextMAS className={s["transaction-info-card-amount"]}>
                {`HKD ${
                  amountConversion(dataSource?.yearlyStats?.[0]?.totalVolume) ??
                  "-"
                } (${
                  amountConversion(dataSource?.yearlyStats?.[0]?.totalCount) ??
                  "-"
                })`}
              </TextMAS>
            </Stack>
          </Card>
        </Stack>
        <div className={s["transaction-year-change-container"]}>
          <TextMAS fontSize="large" fontWeight="semibold">
            {t(PAGE_LOCALE_KEY + "transactionYearChange.title")}
          </TextMAS>
          <AnalyticsTable
            columns={transactionYearChangeColumns}
            data={dataSource?.yearlyStats}
            tableStyle={{ width: "1050px" }}
          />
        </div>
        <div className={s["transaction-month-change-container"]}>
          <TextMAS fontSize="large" fontWeight="semibold">
            {t(PAGE_LOCALE_KEY + "transactionMonthChange.title")}
          </TextMAS>
          <AnalyticsTable
            columns={transactionMonthChangeColumns}
            data={dataSource?.monthlyStats}
            tableStyle={{ width: "1050px" }}
          />
        </div>
      </div>
    </PageContainer>
  );
};

export default React.memo(StatisticsOfflineTransaction);
